import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Loader2, Save, Trash2, Cloud, HardDrive, ArrowUpFromLine, CheckCircle, XCircle, AlertTriangle, Settings2, Eye, EyeOff, Zap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    auto_webp: true,
    webp_quality: 85,
    max_width: 1920,
    max_height: 1920,
    optimize_on_upload: true,
    use_cdn: true,
    cdn_enabled: false
  });
  const [stats, setStats] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // CDN Settings state
  const [cdnSettings, setCdnSettings] = useState({
    storage_provider: 'emergent',
    custom_r2_account_id: '',
    custom_r2_access_key_id: '',
    custom_r2_secret_access_key: '',
    custom_r2_bucket_name: '',
    custom_r2_public_url: '',
    emergent_available: false
  });
  const [savingCdn, setSavingCdn] = useState(false);
  const [testingCdn, setTestingCdn] = useState(false);
  const [cdnTestResult, setCdnTestResult] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  
  // Migration state
  const [migrating, setMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, statsRes, filesRes, cdnRes] = await Promise.all([
        axios.get(`${API}/upload/settings`, { headers }),
        axios.get(`${API}/upload/stats`, { headers }),
        axios.get(`${API}/upload/list`, { headers }),
        axios.get(`${API}/upload/cdn-settings`, { headers })
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setFiles(filesRes.data);
      setCdnSettings(cdnRes.data);
    } catch (error) {
      console.error('Error fetching image settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/upload/settings`, settings, { headers });
      setSettings(res.data);
      alert('Image settings saved!');
    } catch (error) {
      alert('Error saving');
    } finally {
      setSaving(false);
    }
  };

  // CDN Settings functions
  const saveCdnSettings = async () => {
    setSavingCdn(true);
    setCdnTestResult(null);
    try {
      const res = await axios.put(`${API}/upload/cdn-settings`, cdnSettings, { headers });
      setCdnSettings(res.data);
      alert('CDN settings saved!');
      fetchData(); // Refresh all data
    } catch (error) {
      alert('Failed to save CDN settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingCdn(false);
    }
  };

  const testCdnConnection = async () => {
    setTestingCdn(true);
    setCdnTestResult(null);
    try {
      const res = await axios.post(`${API}/upload/cdn-settings/test`, {}, { headers });
      setCdnTestResult(res.data);
    } catch (error) {
      setCdnTestResult({
        success: false,
        message: error.response?.data?.detail || error.message
      });
    } finally {
      setTestingCdn(false);
    }
  };

  const deleteFile = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await axios.delete(`${API}/upload/files/${filename}`, { headers });
      setFiles(files.filter(f => f.filename !== filename));
      fetchData();
    } catch (error) {
      alert('Error deleting');
    }
  };

  // Migration functions
  const startMigration = async () => {
    if (!window.confirm('All local images will be migrated to CDN. Do you want to continue?')) return;
    
    setMigrating(true);
    setMigrationResult(null);
    setMigrationStatus({ running: true, total: stats?.local_files || 0, processed: 0, success: 0, failed: 0 });
    
    try {
      const response = await axios.post(`${API}/upload/migration/start`, {}, { headers });
      setMigrationResult(response.data);
      setMigrationStatus(null);
      fetchData(); // Refresh stats
    } catch (error) {
      setMigrationResult({
        error: true,
        message: error.response?.data?.detail || 'Migration failed'
      });
    } finally {
      setMigrating(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CDN Provider Settings Card */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-blue-400" />
              CDN Provider Settings
            </h2>
            <p className="text-zinc-400 text-sm mt-1">Select image storage provider</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={testCdnConnection}
              disabled={testingCdn}
              variant="outline"
              className="border-zinc-700 text-zinc-300"
            >
              {testingCdn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Test
            </Button>
            <Button
              onClick={saveCdnSettings}
              disabled={savingCdn}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingCdn ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Test Result */}
        {cdnTestResult && (
          <div className={`mb-4 p-3 rounded-lg ${cdnTestResult.success ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center gap-2">
              {cdnTestResult.success ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              <span className={cdnTestResult.success ? 'text-emerald-400' : 'text-red-400'}>
                {cdnTestResult.message}
              </span>
            </div>
          </div>
        )}

        {/* Provider Selection */}
        <div className="space-y-4">
          {/* Emergent Storage Option */}
          <div 
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              cdnSettings.storage_provider === 'emergent' 
                ? 'border-emerald-500 bg-emerald-500/10' 
                : 'border-zinc-700 hover:border-zinc-600'
            }`}
            onClick={() => setCdnSettings({ ...cdnSettings, storage_provider: 'emergent' })}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  cdnSettings.storage_provider === 'emergent' 
                    ? 'border-emerald-500 bg-emerald-500' 
                    : 'border-zinc-500'
                }`}>
                  {cdnSettings.storage_provider === 'emergent' && (
                    <div className="w-full h-full rounded-full bg-white scale-50" />
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">Emergent Object Storage</p>
                  <p className="text-zinc-400 text-sm">Automatic CDN, no additional settings required (Recommended)</p>
                </div>
              </div>
              <Badge className={cdnSettings.emergent_available ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                {cdnSettings.emergent_available ? 'Active' : 'Not Connected'}
              </Badge>
            </div>
          </div>

          {/* Custom R2 Option */}
          <div 
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              cdnSettings.storage_provider === 'custom_r2' 
                ? 'border-blue-500 bg-blue-500/10' 
                : 'border-zinc-700 hover:border-zinc-600'
            }`}
            onClick={() => setCdnSettings({ ...cdnSettings, storage_provider: 'custom_r2' })}
          >
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 ${
                cdnSettings.storage_provider === 'custom_r2' 
                  ? 'border-blue-500 bg-blue-500' 
                  : 'border-zinc-500'
              }`}>
                {cdnSettings.storage_provider === 'custom_r2' && (
                  <div className="w-full h-full rounded-full bg-white scale-50" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">My Own Cloudflare R2 Account</p>
                <p className="text-zinc-400 text-sm">Use your own R2 bucket</p>
              </div>
            </div>
          </div>

          {/* Custom R2 Configuration */}
          {cdnSettings.storage_provider === 'custom_r2' && (
            <div className="mt-4 p-4 bg-zinc-800 rounded-lg space-y-4">
              <p className="text-zinc-300 text-sm mb-4">
                Get your API credentials from Cloudflare Dashboard &gt; R2 &gt; Manage R2 API Tokens.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Account ID</Label>
                  <Input
                    value={cdnSettings.custom_r2_account_id}
                    onChange={(e) => setCdnSettings({ ...cdnSettings, custom_r2_account_id: e.target.value })}
                    className="bg-zinc-700 border-zinc-600 text-white mt-1"
                    placeholder="Cloudflare Account ID"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Bucket Name</Label>
                  <Input
                    value={cdnSettings.custom_r2_bucket_name}
                    onChange={(e) => setCdnSettings({ ...cdnSettings, custom_r2_bucket_name: e.target.value })}
                    className="bg-zinc-700 border-zinc-600 text-white mt-1"
                    placeholder="my-bucket"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Access Key ID</Label>
                <Input
                  value={cdnSettings.custom_r2_access_key_id}
                  onChange={(e) => setCdnSettings({ ...cdnSettings, custom_r2_access_key_id: e.target.value })}
                  className="bg-zinc-700 border-zinc-600 text-white mt-1"
                  placeholder="R2 Access Key ID"
                />
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Secret Access Key</Label>
                <div className="relative">
                  <Input
                    type={showSecretKey ? 'text' : 'password'}
                    value={cdnSettings.custom_r2_secret_access_key}
                    onChange={(e) => setCdnSettings({ ...cdnSettings, custom_r2_secret_access_key: e.target.value })}
                    className="bg-zinc-700 border-zinc-600 text-white mt-1 pr-10"
                    placeholder="R2 Secret Access Key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Public URL (Optional)</Label>
                <Input
                  value={cdnSettings.custom_r2_public_url}
                  onChange={(e) => setCdnSettings({ ...cdnSettings, custom_r2_public_url: e.target.value })}
                  className="bg-zinc-700 border-zinc-600 text-white mt-1"
                  placeholder="https://pub-xxx.r2.dev veya custom domain"
                />
                <p className="text-zinc-500 text-xs mt-1">
                  Public access or custom domain URL for your R2 bucket
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CDN Status Banner */}
      <div className={`rounded-lg border p-4 ${settings.cdn_enabled ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
        <div className="flex items-center gap-3">
          <Cloud className={`w-6 h-6 ${settings.cdn_enabled ? 'text-emerald-400' : 'text-yellow-400'}`} />
          <div>
            <p className={`font-medium ${settings.cdn_enabled ? 'text-emerald-400' : 'text-yellow-400'}`}>
              {settings.cdn_enabled 
                ? (cdnSettings.storage_provider === 'custom_r2' ? 'Custom Cloudflare R2 Active' : 'Emergent CDN Active')
                : 'No CDN Connection'}
            </p>
            <p className="text-zinc-400 text-sm">
              {settings.cdn_enabled 
                ? 'Images are served via global CDN' 
                : 'Images are served from local server'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Card */}
      {stats && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Storage Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-zinc-800 rounded-lg p-4">
              <p className="text-zinc-400 text-sm">Total Files</p>
              <p className="text-2xl font-bold text-white">{stats.total_files}</p>
            </div>
            <div className="bg-zinc-800 rounded-lg p-4">
              <p className="text-zinc-400 text-sm">Total Size</p>
              <p className="text-2xl font-bold text-white">{stats.total_size_mb} MB</p>
            </div>
            {stats.cdn_files > 0 && (
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Cloud className="w-4 h-4 text-emerald-400" />
                  <p className="text-zinc-400 text-sm">CDN</p>
                </div>
                <p className="text-2xl font-bold text-emerald-400">{stats.cdn_files}</p>
                <p className="text-xs text-zinc-500">{stats.cdn_size_mb} MB</p>
              </div>
            )}
            {stats.local_files > 0 && (
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-blue-400" />
                  <p className="text-zinc-400 text-sm">Local</p>
                </div>
                <p className="text-2xl font-bold text-blue-400">{stats.local_files}</p>
                <p className="text-xs text-zinc-500">{stats.local_size_mb} MB</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Migration Card - Show only if there are local files and CDN is enabled */}
      {stats && stats.local_files > 0 && settings.cdn_enabled && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <ArrowUpFromLine className="w-5 h-5 text-blue-400" />
                Migrate to CDN
              </h2>
              <p className="text-zinc-400 text-sm mt-1">
                {stats.local_files} local files ({stats.local_size_mb} MB) to migrate to CDN
              </p>
            </div>
            <Button
              onClick={startMigration}
              disabled={migrating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {migrating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <ArrowUpFromLine className="w-4 h-4 mr-2" />
                  Migrate All to CDN
                </>
              )}
            </Button>
          </div>

          {/* Migration Progress */}
          {migrationStatus && migrationStatus.running && (
            <div className="mt-4 p-4 bg-zinc-800 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-sm">Migrating...</span>
                <span className="text-zinc-400 text-sm">
                  {migrationStatus.processed} / {migrationStatus.total}
                </span>
              </div>
              <Progress 
                value={(migrationStatus.processed / migrationStatus.total) * 100} 
                className="h-2"
              />
            </div>
          )}

          {/* Migration Result */}
          {migrationResult && (
            <div className={`mt-4 p-4 rounded-lg border ${
              migrationResult.error 
                ? 'bg-red-500/10 border-red-500/30' 
                : migrationResult.failed > 0 
                  ? 'bg-yellow-500/10 border-yellow-500/30'
                  : 'bg-emerald-500/10 border-emerald-500/30'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {migrationResult.error ? (
                  <XCircle className="w-5 h-5 text-red-400" />
                ) : migrationResult.failed > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                )}
                <span className={`font-medium ${
                  migrationResult.error ? 'text-red-400' : 
                  migrationResult.failed > 0 ? 'text-yellow-400' : 'text-emerald-400'
                }`}>
                  {migrationResult.error ? 'Error!' : 
                   migrationResult.failed > 0 ? 'Partially Completed' : 'Completed!'}
                </span>
              </div>
              
              {migrationResult.error ? (
                <p className="text-zinc-300 text-sm">{migrationResult.message}</p>
              ) : (
                <div className="space-y-1 text-sm">
                  <p className="text-zinc-300">
                    <span className="text-emerald-400">{migrationResult.success}</span> files migrated successfully
                    {migrationResult.failed > 0 && (
                      <span className="text-red-400 ml-2">({migrationResult.failed} failed)</span>
                    )}
                  </p>
                  {migrationResult.errors && migrationResult.errors.length > 0 && (
                    <div className="mt-2 p-2 bg-zinc-900 rounded text-xs">
                      <p className="text-red-400 mb-1">Errors:</p>
                      {migrationResult.errors.map((err, i) => (
                        <p key={i} className="text-zinc-400">{err.filename}: {err.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Optimization Settings */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Image Optimization</h2>
            <p className="text-zinc-400 text-sm mt-1">Automatic optimization settings for uploaded images</p>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Use CDN */}
          <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
            <div>
              <p className="text-white font-medium">Use CDN (Cloudflare R2)</p>
              <p className="text-zinc-400 text-sm">Serve images via global CDN (faster loading)</p>
            </div>
            <Switch
              checked={settings.use_cdn}
              onCheckedChange={(checked) => setSettings({ ...settings, use_cdn: checked })}
              disabled={!settings.cdn_enabled}
            />
          </div>

          {/* Auto WebP */}
          <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
            <div>
              <p className="text-white font-medium">Auto WebP Conversion</p>
              <p className="text-zinc-400 text-sm">Uploaded images are automatically converted to WebP format</p>
            </div>
            <Switch
              checked={settings.auto_webp}
              onCheckedChange={(checked) => setSettings({ ...settings, auto_webp: checked })}
            />
          </div>

          {/* Optimize on Upload */}
          <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
            <div>
              <p className="text-white font-medium">Optimize on Upload</p>
              <p className="text-zinc-400 text-sm">Images are automatically resized during upload</p>
            </div>
            <Switch
              checked={settings.optimize_on_upload}
              onCheckedChange={(checked) => setSettings({ ...settings, optimize_on_upload: checked })}
            />
          </div>

          {/* WebP Quality */}
          {settings.auto_webp && (
            <div className="p-4 bg-zinc-800 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-medium">WebP Quality</p>
                <span className="text-emerald-400 font-bold">{settings.webp_quality}%</span>
              </div>
              <input
                type="range"
                min="10"
                max="100"
                value={settings.webp_quality}
                onChange={(e) => setSettings({ ...settings, webp_quality: parseInt(e.target.value) })}
                className="w-full accent-emerald-500"
              />
              <div className="flex justify-between text-zinc-500 text-xs mt-1">
                <span>Low (Small size)</span>
                <span>High (Good quality)</span>
              </div>
            </div>
          )}

          {/* Max Dimensions */}
          <div className="p-4 bg-zinc-800 rounded-lg">
            <p className="text-white font-medium mb-4">Maximum Dimensions</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300 text-sm">Maximum Width (px)</Label>
                <Input
                  type="number"
                  min="100"
                  max="4096"
                  value={settings.max_width}
                  onChange={(e) => setSettings({ ...settings, max_width: parseInt(e.target.value) || 1920 })}
                  className="bg-zinc-700 border-zinc-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-sm">Maximum Height (px)</Label>
                <Input
                  type="number"
                  min="100"
                  max="4096"
                  value={settings.max_height}
                  onChange={(e) => setSettings({ ...settings, max_height: parseInt(e.target.value) || 1920 })}
                  className="bg-zinc-700 border-zinc-600 text-white mt-1"
                />
              </div>
            </div>
            <p className="text-zinc-500 text-xs mt-2">
              Images exceeding these dimensions are automatically resized
            </p>
          </div>
        </div>
      </div>

      {/* Uploaded Files List */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Uploaded Images ({files.length})</h2>
        
        {files.length === 0 ? (
          <p className="text-zinc-400 text-center py-8">No uploaded images yet</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {files.slice(0, 24).map((file) => (
              <div key={file.filename} className="group relative">
                <div className="aspect-square bg-zinc-800 rounded-lg overflow-hidden">
                  <img
                    src={`${BACKEND_URL}${file.url}`}
                    alt={file.filename}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center">
                  <div className="flex items-center gap-1 mb-1">
                    {file.storage === 'cdn' ? (
                      <Cloud className="w-3 h-3 text-emerald-400" />
                    ) : (
                      <HardDrive className="w-3 h-3 text-blue-400" />
                    )}
                    <Badge className="bg-emerald-500 text-white">{file.format}</Badge>
                  </div>
                  <p className="text-white text-xs">{formatSize(file.size)}</p>
                  <button
                    onClick={() => deleteFile(file.filename)}
                    className="mt-2 p-1 bg-red-500 rounded hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {files.length > 24 && (
          <p className="text-zinc-400 text-center mt-4 text-sm">
            +{files.length - 24} more images...
          </p>
        )}
      </div>
    </div>
  );
};

export default ImageSettingsTab;
